import React, { useEffect, useMemo, useState } from 'react';
import { ScrollView, View, StyleSheet, Platform, Dimensions, Modal } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Button, Dialog, Portal, Text, TextInput, useTheme, Chip, Surface, IconButton } from 'react-native-paper';
import dayjs from 'dayjs';
import { getTimeLogs, TimeLogEntry, updateTimeLogEntry } from '../api/apiService';
import { reportDayKey } from '../utils/reportDay';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { LoadingScreen } from '@/src/components/LoadingScreen';
import { Appbar } from 'react-native-paper';


const debugLog = (...args: any[]) => {
  if (__DEV__) console.log(...args);
};

interface EditDayEventsModalProps {
  visible: boolean;
  date: string; // YYYY-MM-DD
  onDismiss: () => void;
  onSaved?: () => void;
}
type EditableEntry = Pick<TimeLogEntry, 'id' | 'timestamp' | 'event_type' | 'vehicle_registration_number' | 'odometer'> & {
  wasEdited: boolean;
  originalObservations: string | null;
  originalOdometer: number | null;
  originalTimestamp: string;
  initialOdometerStr: string; // snapshot used to detect actual user edits
  initialTimestampStr: string; // 'DD/MM/YYYY HH:mm'
  newOdometer: string; // for session_in/session_out
  newTimestamp: string; // for drive_in/drive_out (DD/MM/YYYY HH:MM)
  newObservation: string; // persisted only when changed
  observationError?: boolean;
};

export const EditDayEventsModal: React.FC<EditDayEventsModalProps> = ({ visible, date, onDismiss, onSaved }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState<EditableEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [expandedEntries, setExpandedEntries] = useState<Set<number>>(new Set());
  const observationRefs = React.useRef<Record<number, any>>({});

  const screenWidth = Dimensions.get('window').width;
  const isSmallScreen = screenWidth < 768;
  const isFullScreen = Platform.OS !== 'web' || isSmallScreen;

  const getDialogStyle = () => {
    if (isFullScreen) {
      return {
        position: 'absolute' as any,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        maxHeight: '100%',
        height: '100%' as any,
        maxWidth: '100%',
        width: '100%' as any,
        borderRadius: 0,
        margin: 0,
        padding: 0,
      } as any;
    }
    return styles.dialog;
  };

  const title = useMemo(() => `${t('dailyDrivingReport')} - ${date}`, [date, t]);
  const anyEdited = useMemo(() => entries.some((e) => e.wasEdited), [entries]);

  useEffect(() => {
    const load = async () => {
      if (!visible) return;
      setLoading(true);
      setError(null);
      try {
        const resp = await getTimeLogs();
        if (resp.ok) {
          const filtered = resp.data
            // `date` is a report-zone key (#300); dayjs would format in the
            // device zone, which is not the zone the report is grouped by.
            .filter((e) => reportDayKey(new Date(e.timestamp)) === date)
            .sort((a, b) => dayjs(a.timestamp).valueOf() - dayjs(b.timestamp).valueOf())
            .map<EditableEntry>((e) => ({
              id: e.id,
              timestamp: e.timestamp,
              event_type: e.event_type,
              vehicle_registration_number: e.vehicle_registration_number,
              odometer: e.odometer,
              wasEdited: !!(e as any).edited,
              originalObservations: (e as any).observations ?? null,
              originalOdometer: e.odometer,
              originalTimestamp: e.timestamp,
              initialOdometerStr: e.odometer != null ? String(e.odometer) : '',
              initialTimestampStr: dayjs(e.timestamp).format('DD/MM/YYYY HH:mm'),
              newOdometer: e.odometer != null ? String(e.odometer) : '',
              newTimestamp: dayjs(e.timestamp).format('DD/MM/YYYY HH:mm'),
              newObservation: e.observations || '',
            }));
          setEntries(filtered);
        } else {
          setError(typeof resp.data === 'object' && 'detail' in (resp.data as any) ? (resp.data as any).detail : t('error'));
        }
      } catch (e: any) {
        setError(e?.message || String(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [visible, date, t]);

  const toggleExpanded = (entryId: number) => {
    setExpandedEntries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entryId)) {
        newSet.delete(entryId);
      } else {
        newSet.add(entryId);
      }
      return newSet;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      let firstMissingObservationId: number | null = null;
      let hadFailure = false;
      let anyChanges = false;
      debugLog('Starting save process with', entries.length, 'entries');
      
      for (const entry of entries) {
        if (entry.event_type === 'session_in' || entry.event_type === 'session_out') {
          const parsed = entry.newOdometer.trim() === '' ? null : Number(entry.newOdometer);
          const changed = parsed !== entry.originalOdometer;
          debugLog('Session entry', entry.id, 'changed:', changed, 'odometer:', entry.originalOdometer, '->', parsed);
          if (changed) {
            anyChanges = true;
            if (!entry.newObservation.trim()) {
              setEntries((prev) => prev.map((x) => (x.id === entry.id ? { ...x, observationError: true } : x)));
              if (firstMissingObservationId === null) firstMissingObservationId = entry.id;
              continue;
            }
            const resp = await updateTimeLogEntry(entry.id, { odometer: parsed as number | null, edited: true, observations: entry.newObservation });
            if (!resp.ok) {
              const msg = (resp.data as any)?.odometer || (resp.data as any)?.detail || t('error');
              setEntries((prev) => prev.map((x) => (x.id === entry.id ? { ...x, observationError: true } : x)));
              setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
              hadFailure = true;
              continue;
            }
          }
        } else if (entry.event_type === 'drive_in' || entry.event_type === 'drive_out') {
          // Parse DD/MM/YYYY HH:MM format
          const timestampMatch = entry.newTimestamp.match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})$/);
          debugLog('Drive entry', entry.id, 'timestamp:', entry.newTimestamp, 'match:', !!timestampMatch);
          if (timestampMatch) {
            const [, day, month, year, hour, minute] = timestampMatch;
            const newDate = dayjs(`${year}-${month}-${day} ${hour}:${minute}`, 'YYYY-MM-DD HH:mm');
            // Compare only up to minute precision to avoid false positives from seconds/milliseconds
            const originalFormatted = dayjs(entry.originalTimestamp).format('YYYY-MM-DD HH:mm');
            const newFormatted = newDate.format('YYYY-MM-DD HH:mm');
            const changed = newDate.isValid() && newFormatted !== originalFormatted;
            debugLog('Drive entry', entry.id, 'changed:', changed, 'original formatted:', originalFormatted, 'new formatted:', newFormatted);
            
            if (changed) {
              anyChanges = true;
              if (!entry.newObservation.trim()) {
                setEntries((prev) => prev.map((x) => (x.id === entry.id ? { ...x, observationError: true } : x)));
                if (firstMissingObservationId === null) firstMissingObservationId = entry.id;
                continue;
              }
              const resp = await updateTimeLogEntry(entry.id, { edited: true, observations: entry.newObservation, timestamp: newDate.toISOString() } as any);
              if (!resp.ok) {
                const msg = (resp.data as any)?.odometer || (resp.data as any)?.detail || t('error');
                setEntries((prev) => prev.map((x) => (x.id === entry.id ? { ...x, observationError: true } : x)));
                setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
                hadFailure = true;
                continue;
              }
            }
          }
        }
      }
      debugLog('Finished processing all entries. firstMissingObservationId:', firstMissingObservationId, 'hadFailure:', hadFailure, 'anyChanges:', anyChanges);
      
      if (firstMissingObservationId !== null) {
        debugLog('Validation error: missing observation for entry', firstMissingObservationId, 'keeping modal open');
        setSaving(false);
        // Focus the first missing observation field and keep the modal open
        setTimeout(() => {
          try {
            observationRefs.current[firstMissingObservationId!]?.focus?.();
          } catch {}
        }, 0);
        return;
      }
      if (hadFailure) {
        debugLog('Server validation error, keeping modal open');
        // Keep modal open; user must resolve server-side validation issues
        setSaving(false);
        return;
      }
      
      debugLog('All validation passed, closing modal');
      // Close modal whether changes were made or not (user clicked save, so they want to close)
      if (onSaved) onSaved();
      onDismiss();
    } catch (e: any) {
      setError(e?.message || String(e));
      setSaving(false);
    }
  };

  const formatEventLabel = (type: string) => {
    switch (type) {
      case 'session_in':
        return t('eventLabels.session_in', { defaultValue: 'Início de sessão' });
      case 'session_out':
        return t('eventLabels.session_out', { defaultValue: 'Fim de sessão' });
      case 'drive_in':
        return t('eventLabels.drive_in', { defaultValue: 'Início de condução' });
      case 'drive_out':
        return t('eventLabels.drive_out', { defaultValue: 'Fim de condução' });
      default:
        return type;
    }
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'session_in':
        return 'play-circle';
      case 'session_out':
        return 'stop-circle';
      case 'drive_in':
        return 'car';
      case 'drive_out':
        return 'car-off';
      default:
        return 'circle';
    }
  };

  const getEventColor = (type: string) => {
    switch (type) {
      case 'session_in':
        return theme.colors.primary;
      case 'session_out':
        return theme.colors.error;
      case 'drive_in':
        return theme.dark ? theme.colors.onTertiaryContainer : theme.colors.tertiary;
      case 'drive_out':
        return theme.colors.secondary;
      default:
        return theme.colors.outline;
    }
  };

  return (
    <>
      {isFullScreen ? (
        <Modal visible={visible} transparent={false} animationType="slide" onRequestClose={onDismiss} statusBarTranslucent>
          <SafeAreaView style={{ flex: 1, backgroundColor: theme.colors.background }}>
            <Appbar.Header style={{ backgroundColor: theme.colors.surface }}>
              <Appbar.BackAction onPress={onDismiss} />
              <Appbar.Content title={title} />
            </Appbar.Header>
            
            {loading ? (
              <LoadingScreen fullScreen={false} />
            ) : (
              <>
                <ScrollView 
                  showsVerticalScrollIndicator={true}
                  contentContainerStyle={[styles.scrollContent, { paddingHorizontal: 16 }]}
                  style={{ flex: 1 }}
                >
                  {entries.length === 0 ? (
                    <View style={styles.emptyContainer}>
                      <Text variant="bodyLarge" style={{ color: theme.colors.onSurfaceVariant }}>
                        {t('noReport')}
                      </Text>
                    </View>
                  ) : (
                    <View style={styles.entriesContainer}>
                      {entries.map((e) => {
                        const isSession = e.event_type === 'session_in' || e.event_type === 'session_out';
                        const isDrive = e.event_type === 'drive_in' || e.event_type === 'drive_out';
                        const isExpanded = expandedEntries.has(e.id);
                        
                        // Show "Edited" if previously edited in backend; show "Unsaved" if user changed now
                        const changed = isSession
                          ? e.newOdometer.trim() !== e.initialOdometerStr.trim()
                          : (() => {
                              // For drive entries, compare only up to minute precision
                              if (e.event_type === 'drive_in' || e.event_type === 'drive_out') {
                                const timestampMatch = e.newTimestamp.match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})$/);
                                if (timestampMatch) {
                                  const [, day, month, year, hour, minute] = timestampMatch;
                                  const newDate = dayjs(`${year}-${month}-${day} ${hour}:${minute}`, 'YYYY-MM-DD HH:mm');
                                  const originalFormatted = dayjs(e.timestamp).format('YYYY-MM-DD HH:mm');
                                  const newFormatted = newDate.format('YYYY-MM-DD HH:mm');
                                  return newDate.isValid() && newFormatted !== originalFormatted;
                                }
                              }
                              return false;
                            })();

                        return (
                          <Surface key={e.id} style={[styles.eventCard, { backgroundColor: theme.colors.surfaceVariant }]} elevation={2}>
                            <View style={styles.eventHeader}>
                              <View style={styles.eventInfo}>
                                <View style={[styles.eventIcon, { backgroundColor: getEventColor(e.event_type) }]}>
                                  <IconButton
                                    icon={getEventIcon(e.event_type)}
                                    iconColor={theme.colors.surface}
                                    size={20}
                                    style={styles.iconButton}
                                  />
                                </View>
                                <View style={styles.eventDetails}>
                                  <Text variant="titleMedium" style={[styles.eventTitle, { color: theme.colors.onSurfaceVariant }]}>
                                    {formatEventLabel(e.event_type)}
                                  </Text>
                                  <Text variant="bodyMedium" style={[styles.eventTime, { color: theme.colors.onSurface }]}>
                                    {dayjs(e.timestamp).format('HH:mm')}
                                    {e.vehicle_registration_number && ` · ${e.vehicle_registration_number}`}
                                    {isSession && ` · ${t('odometer')}: ${e.newOdometer || '-'}`}
                                  </Text>
                                </View>
                              </View>
                              
                              <View style={styles.eventActions}>
                                {e.wasEdited && (
                                  <Chip 
                                    compact 
                                    style={[styles.statusChip, { backgroundColor: theme.colors.primaryContainer }]}
                                    textStyle={{ color: theme.colors.onPrimaryContainer }}
                                  >
                                    {t('edited', { defaultValue: 'Editado' })}
                                  </Chip>
                                )}
                                {changed && (
                                  <Chip 
                                    compact 
                                    mode="outlined" 
                                    style={[styles.statusChip, { borderColor: theme.colors.primary }]}
                                    textStyle={{ color: theme.colors.primary }}
                                  >
                                    {t('unsaved', { defaultValue: 'Por guardar' })}
                                  </Chip>
                                )}
                                <IconButton
                                  icon={isExpanded ? 'chevron-up' : 'chevron-down'}
                                  onPress={() => toggleExpanded(e.id)}
                                  iconColor={theme.colors.onSurfaceVariant}
                                  size={24}
                                />
                              </View>
                            </View>

                            {isExpanded && (
                              <View style={styles.eventForm}>
                                <View style={styles.formSection}>
                                  {isSession ? (
                                    <TextInput
                                      mode="outlined"
                                      label={`${t('odometer')} (km)`}
                                      keyboardType="numeric"
                                      value={e.newOdometer}
                                      onChangeText={(txt) => setEntries((prev) => prev.map((x) => (x.id === e.id ? { ...x, newOdometer: txt } : x)))}
                                      style={styles.input}
                                      contentStyle={styles.inputContent}
                                    />
                                  ) : (
                                    <TextInput
                                      mode="outlined"
                                      label={`${t('date')} & ${t('time')} (DD/MM/YYYY HH:MM)`}
                                      value={e.newTimestamp}
                                      onChangeText={(txt) => {
                                        // Format validation: DD/MM/YYYY HH:MM
                                        const formatted = txt.replace(/[^0-9]/g, ''); // Remove non-digits
                                        let result = '';
                                        if (formatted.length >= 1) result += formatted[0];
                                        if (formatted.length >= 2) result += formatted[1] + '/';
                                        if (formatted.length >= 3) result += formatted[2];
                                        if (formatted.length >= 4) result += formatted[3] + '/';
                                        if (formatted.length >= 5) result += formatted[4];
                                        if (formatted.length >= 6) result += formatted[5];
                                        if (formatted.length >= 7) result += formatted[6];
                                        if (formatted.length >= 8) result += formatted[7] + ' ';
                                        if (formatted.length >= 9) result += formatted[8];
                                        if (formatted.length >= 10) result += formatted[9] + ':';
                                        if (formatted.length >= 11) result += formatted[10];
                                        if (formatted.length >= 12) result += formatted[11];
                                        
                                        setEntries((prev) => prev.map((x) => (x.id === e.id ? { ...x, newTimestamp: result } : x)));
                                      }}
                                      placeholder="DD/MM/YYYY HH:MM"
                                      keyboardType="numeric"
                                      maxLength={16} // DD/MM/YYYY HH:MM = 16 characters
                                      error={!!(e.newTimestamp && !e.newTimestamp.match(/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/))}
                                      style={styles.input}
                                      contentStyle={styles.inputContent}
                                    />
                                  )}
                                  
                                  <TextInput
                                    mode="outlined"
                                    label={t('observations') + (changed ? ' *' : '')}
                                    placeholder={t('observationsRequiredWhenEdited', { defaultValue: 'Obrigatório quando editado' }) as string}
                                    value={e.newObservation}
                                    onChangeText={(txt) => setEntries((prev) => prev.map((x) => (x.id === e.id ? { ...x, newObservation: txt, observationError: false } : x)))}
                                    style={[styles.input, styles.observationInput]}
                                    contentStyle={styles.inputContent}
                                    error={!!e.observationError}
                                    ref={(r: any) => {
                                      if (r) {
                                        observationRefs.current[e.id] = r;
                                      }
                                    }}
                                    multiline
                                    numberOfLines={3}
                                  />
                                </View>
                              </View>
                            )}
                          </Surface>
                        );
                      })}
                    </View>
                  )}
                  
                  {error && (
                    <Surface style={[styles.errorContainer, { backgroundColor: theme.colors.errorContainer }]} elevation={1}>
                      <Text style={[styles.errorText, { color: theme.colors.onErrorContainer }]}>{error}</Text>
                    </Surface>
                  )}
                </ScrollView>
                
                <View style={{ paddingHorizontal: 24, paddingBottom: 12, paddingTop: 12, gap: 12, flexDirection: 'row' }}>
                  <Button 
                    onPress={onDismiss}
                    mode="outlined"
                    style={{ flex: 1 }}
                  >
                    {t('close')}
                  </Button>
                  <Button 
                    onPress={handleSave} 
                    loading={saving} 
                    disabled={saving || loading}
                    mode="contained"
                    style={{ flex: 1 }}
                  >
                    {t('save', { defaultValue: 'Save' })}
                  </Button>
                </View>
              </>
            )}
          </SafeAreaView>
        </Modal>
      ) : (
        <Portal>
          <Dialog visible={visible} onDismiss={onDismiss} style={[getDialogStyle(), { flexDirection: 'column' }]}>
            <Dialog.Title style={[styles.dialogTitle, { paddingHorizontal: 16, paddingTop: 16, paddingBottom: 12, flexShrink: 0 }]}>
              <View style={styles.titleContainer}>
                <Text variant="titleLarge" style={styles.titleText}>{title}</Text>
              </View>
            </Dialog.Title>
            
            <Dialog.Content style={[styles.dialogContent, { paddingHorizontal: 24, paddingVertical: 0, flex: 1 }]}>
              {loading ? (
                <LoadingScreen fullScreen={false} />
              ) : (
                <View style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <ScrollView 
                    showsVerticalScrollIndicator={true}
                    contentContainerStyle={[styles.scrollContent, { paddingHorizontal: 0 }]}
                    style={styles.scrollView}
                  >
                    {entries.length === 0 ? (
                      <View style={styles.emptyContainer}>
                        <Text variant="bodyLarge" style={{ color: theme.colors.onSurfaceVariant }}>
                          {t('noReport')}
                        </Text>
                      </View>
                    ) : (
                      <View style={styles.entriesContainer}>
                        {entries.map((e) => {
                          const isSession = e.event_type === 'session_in' || e.event_type === 'session_out';
                          const isDrive = e.event_type === 'drive_in' || e.event_type === 'drive_out';
                          const isExpanded = expandedEntries.has(e.id);
                          
                          // Show "Edited" if previously edited in backend; show "Unsaved" if user changed now
                          const changed = isSession
                            ? e.newOdometer.trim() !== e.initialOdometerStr.trim()
                            : (() => {
                                // For drive entries, compare only up to minute precision
                                if (e.event_type === 'drive_in' || e.event_type === 'drive_out') {
                                  const timestampMatch = e.newTimestamp.match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})$/);
                                  if (timestampMatch) {
                                    const [, day, month, year, hour, minute] = timestampMatch;
                                    const newDate = dayjs(`${year}-${month}-${day} ${hour}:${minute}`, 'YYYY-MM-DD HH:mm');
                                    const originalFormatted = dayjs(e.timestamp).format('YYYY-MM-DD HH:mm');
                                    const newFormatted = newDate.format('YYYY-MM-DD HH:mm');
                                    return newDate.isValid() && newFormatted !== originalFormatted;
                                  }
                                }
                                return false;
                              })();

                          return (
                            <Surface key={e.id} style={[styles.eventCard, { backgroundColor: theme.colors.surfaceVariant }]} elevation={2}>
                              <View style={styles.eventHeader}>
                                <View style={styles.eventInfo}>
                                  <View style={[styles.eventIcon, { backgroundColor: getEventColor(e.event_type) }]}>
                                    <IconButton
                                      icon={getEventIcon(e.event_type)}
                                      iconColor={theme.colors.surface}
                                      size={20}
                                      style={styles.iconButton}
                                    />
                                  </View>
                                  <View style={styles.eventDetails}>
                                    <Text variant="titleMedium" style={[styles.eventTitle, { color: theme.colors.onSurfaceVariant }]}>
                                      {formatEventLabel(e.event_type)}
                                    </Text>
                                    <Text variant="bodyMedium" style={[styles.eventTime, { color: theme.colors.onSurface }]}>
                                      {dayjs(e.timestamp).format('HH:mm')}
                                      {e.vehicle_registration_number && ` · ${e.vehicle_registration_number}`}
                                      {isSession && ` · ${t('odometer')}: ${e.newOdometer || '-'}`}
                                    </Text>
                                  </View>
                                </View>
                                
                                <View style={styles.eventActions}>
                                  {e.wasEdited && (
                                    <Chip 
                                      compact 
                                      style={[styles.statusChip, { backgroundColor: theme.colors.primaryContainer }]}
                                      textStyle={{ color: theme.colors.onPrimaryContainer }}
                                    >
                                      {t('edited', { defaultValue: 'Editado' })}
                                    </Chip>
                                  )}
                                  {changed && (
                                    <Chip 
                                      compact 
                                      mode="outlined" 
                                      style={[styles.statusChip, { borderColor: theme.colors.primary }]}
                                      textStyle={{ color: theme.colors.primary }}
                                    >
                                      {t('unsaved', { defaultValue: 'Por guardar' })}
                                    </Chip>
                                  )}
                                  <IconButton
                                    icon={isExpanded ? 'chevron-up' : 'chevron-down'}
                                    onPress={() => toggleExpanded(e.id)}
                                    iconColor={theme.colors.onSurfaceVariant}
                                    size={24}
                                  />
                                </View>
                              </View>

                              {isExpanded && (
                                <View style={styles.eventForm}>
                                  <View style={styles.formSection}>
                                    {isSession ? (
                                      <TextInput
                                        mode="outlined"
                                        label={`${t('odometer')} (km)`}
                                        keyboardType="numeric"
                                        value={e.newOdometer}
                                        onChangeText={(txt) => setEntries((prev) => prev.map((x) => (x.id === e.id ? { ...x, newOdometer: txt } : x)))}
                                        style={styles.input}
                                        contentStyle={styles.inputContent}
                                      />
                                    ) : (
                                      <TextInput
                                        mode="outlined"
                                        label={`${t('date')} & ${t('time')} (DD/MM/YYYY HH:MM)`}
                                        value={e.newTimestamp}
                                        onChangeText={(txt) => {
                                          // Format validation: DD/MM/YYYY HH:MM
                                          const formatted = txt.replace(/[^0-9]/g, ''); // Remove non-digits
                                          let result = '';
                                          if (formatted.length >= 1) result += formatted[0];
                                          if (formatted.length >= 2) result += formatted[1] + '/';
                                          if (formatted.length >= 3) result += formatted[2];
                                          if (formatted.length >= 4) result += formatted[3] + '/';
                                          if (formatted.length >= 5) result += formatted[4];
                                          if (formatted.length >= 6) result += formatted[5];
                                          if (formatted.length >= 7) result += formatted[6];
                                          if (formatted.length >= 8) result += formatted[7] + ' ';
                                          if (formatted.length >= 9) result += formatted[8];
                                          if (formatted.length >= 10) result += formatted[9] + ':';
                                          if (formatted.length >= 11) result += formatted[10];
                                          if (formatted.length >= 12) result += formatted[11];
                                          
                                          setEntries((prev) => prev.map((x) => (x.id === e.id ? { ...x, newTimestamp: result } : x)));
                                        }}
                                        placeholder="DD/MM/YYYY HH:MM"
                                        keyboardType="numeric"
                                        maxLength={16} // DD/MM/YYYY HH:MM = 16 characters
                                        error={!!(e.newTimestamp && !e.newTimestamp.match(/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/))}
                                        style={styles.input}
                                        contentStyle={styles.inputContent}
                                      />
                                    )}
                                    
                                    <TextInput
                                      mode="outlined"
                                      label={t('observations') + (changed ? ' *' : '')}
                                      placeholder={t('observationsRequiredWhenEdited', { defaultValue: 'Obrigatório quando editado' }) as string}
                                      value={e.newObservation}
                                      onChangeText={(txt) => setEntries((prev) => prev.map((x) => (x.id === e.id ? { ...x, newObservation: txt, observationError: false } : x)))}
                                      style={[styles.input, styles.observationInput]}
                                      contentStyle={styles.inputContent}
                                      error={!!e.observationError}
                                      ref={(r: any) => {
                                        if (r) {
                                          observationRefs.current[e.id] = r;
                                        }
                                      }}
                                      multiline
                                      numberOfLines={3}
                                    />
                                  </View>
                                </View>
                              )}
                            </Surface>
                          );
                        })}
                      </View>
                    )}
                    
                    {error && (
                      <Surface style={[styles.errorContainer, { backgroundColor: theme.colors.errorContainer }]} elevation={1}>
                        <Text style={[styles.errorText, { color: theme.colors.onErrorContainer }]}>{error}</Text>
                      </Surface>
                    )}
                  </ScrollView>
                  
                  <View style={{ paddingHorizontal: 24, paddingBottom: 12, paddingTop: 12, gap: 12, flexDirection: 'row' }}>
                    <Button 
                      onPress={onDismiss}
                      mode="outlined"
                      style={{ flex: 1 }}
                    >
                      {t('close')}
                    </Button>
                    <Button 
                      onPress={handleSave} 
                      loading={saving} 
                      disabled={saving || loading}
                      mode="contained"
                      style={{ flex: 1 }}
                    >
                      {t('save', { defaultValue: 'Save' })}
                    </Button>
                  </View>
                </View>
              )}
            </Dialog.Content>
            
          </Dialog>
        </Portal>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  dialog: {
    maxHeight: '80%',
    borderRadius: 16,
    ...(Platform.OS === 'web' ? {
      maxWidth: 800,
      width: '90%',
      alignSelf: 'center',
    } : {}),
  },
  dialogTitle: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 16,
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleText: {
    flex: 1,
    marginRight: 8,
  },
  editedChip: {
    alignSelf: 'flex-start',
  },
  dialogContent: {
    paddingHorizontal: 24,
    paddingVertical: 0,
    maxHeight: 'auto',
    flex: Platform.OS === 'ios' || Platform.OS === 'android' ? 1 : undefined,
  },
  loadingContainer: {
    paddingVertical: 40,
    alignItems: 'center',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 20,
    flexGrow: 1,
  },
  emptyContainer: {
    paddingVertical: 40,
    alignItems: 'center',
  },
  entriesContainer: {
    gap: 16,
  },
  eventCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 0,
  },
  eventHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  eventInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  eventIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  iconButton: {
    margin: 0,
  },
  eventDetails: {
    flex: 1,
  },
  eventTitle: {
    fontWeight: '600',
    marginBottom: 2,
  },
  eventTime: {
    opacity: 0.8,
  },
  eventActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusChip: {
    marginRight: 8,
  },
  eventForm: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0, 0, 0, 0.08)',
  },
  formSection: {
    gap: 12,
  },
  input: {
    backgroundColor: 'transparent',
  },
  inputContent: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  observationInput: {
    marginTop: 4,
  },
  errorContainer: {
    marginTop: 16,
    padding: 16,
    borderRadius: 8,
  },
  errorText: {
    textAlign: 'center',
  },
  dialogActions: {
    paddingHorizontal: 24,
    paddingBottom: 24,
    paddingTop: 24,
    gap: 12,
    backgroundColor: 'transparent',
  },
  actionButton: {
    minWidth: 100,
    flex: 1,
  },
});

export default EditDayEventsModal;


